'use client';

import { useCallback, useRef, useState } from 'react';

export const CONTEXT_TABS_SNAPSHOT_VERSION = 1;
export const DEFAULT_CONTEXT_TABS_LIMIT = 8;

export type ContextTabLocation = {
  url: string;
  area: string;
  pageKey: string;
  name: string;
  section?: string;
  contextLabel?: string;
  iconKey?: string;
};

export type ContextTab = ContextTabLocation & {
  id: string;
  history: ContextTabLocation[];
  historyIndex: number;
  isDirty: boolean;
  lastActivatedAt: number;
};

export type ContextTabsSnapshot = {
  version: typeof CONTEXT_TABS_SNAPSHOT_VERSION;
  activeTabId: string;
  tabs: ContextTab[];
};

export type ContextTabsCreateResult =
  | { status: 'created'; tabId: string }
  | { status: 'focused'; tabId: string }
  | { status: 'max-tabs' };

export type ContextTabsCloseResult =
  | { status: 'closed'; activeTabId: string }
  | { status: 'dirty' }
  | { status: 'last-tab' }
  | { status: 'not-found' };

export type ContextTabsHistoryResult =
  | { status: 'navigated'; location: ContextTabLocation }
  | { status: 'boundary' };

export type UseContextTabsOptions = {
  initialLocation: ContextTabLocation;
  initialSnapshot?: unknown;
  maxTabs?: number;
  createId?: () => string;
  now?: () => number;
};

type NavigateOptions = {
  mode?: 'push' | 'replace';
  preserveIdentity?: boolean;
};

type CloseOptions = {
  discardDirty?: boolean;
};

type MetadataUpdate = Partial<
  Pick<ContextTabLocation, 'name' | 'section' | 'contextLabel' | 'iconKey'>
>;

const defaultCreateId = () => crypto.randomUUID();

function isLocation(value: unknown): value is ContextTabLocation {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<ContextTabLocation>;

  return (
    typeof candidate.url === 'string' &&
    candidate.url.startsWith('/') &&
    typeof candidate.area === 'string' &&
    candidate.area.length > 0 &&
    typeof candidate.pageKey === 'string' &&
    candidate.pageKey.length > 0 &&
    typeof candidate.name === 'string' &&
    candidate.name.length > 0 &&
    (candidate.section === undefined ||
      typeof candidate.section === 'string') &&
    (candidate.contextLabel === undefined ||
      typeof candidate.contextLabel === 'string') &&
    (candidate.iconKey === undefined || typeof candidate.iconKey === 'string')
  );
}

function copyLocation(location: ContextTabLocation): ContextTabLocation {
  const { url, area, pageKey, name, section, contextLabel, iconKey } = location;
  return { url, area, pageKey, name, section, contextLabel, iconKey };
}

function currentLocation(tab: ContextTab): ContextTabLocation {
  const { url, area, pageKey, name, section, contextLabel, iconKey } = tab;
  return { url, area, pageKey, name, section, contextLabel, iconKey };
}

function withCurrentLocation(
  tab: ContextTab,
  location: ContextTabLocation,
): ContextTab {
  return { ...tab, ...copyLocation(location) };
}

function createTab(
  id: string,
  location: ContextTabLocation,
  activatedAt: number,
  history: ContextTabLocation[] = [location],
  historyIndex = history.length - 1,
): ContextTab {
  return {
    id,
    ...copyLocation(location),
    history: history.map(copyLocation),
    historyIndex,
    isDirty: false,
    lastActivatedAt: activatedAt,
  };
}

function createInitialSnapshot(
  location: ContextTabLocation,
  createId: () => string,
  now: () => number,
): ContextTabsSnapshot {
  const tab = createTab(createId(), location, now());
  return {
    version: CONTEXT_TABS_SNAPSHOT_VERSION,
    activeTabId: tab.id,
    tabs: [tab],
  };
}

function parseSnapshot(
  value: unknown,
  maxTabs: number,
): ContextTabsSnapshot | null {
  if (!value || typeof value !== 'object') return null;
  const candidate = value as Partial<ContextTabsSnapshot>;

  if (
    candidate.version !== CONTEXT_TABS_SNAPSHOT_VERSION ||
    typeof candidate.activeTabId !== 'string' ||
    !Array.isArray(candidate.tabs) ||
    candidate.tabs.length === 0 ||
    candidate.tabs.length > maxTabs
  ) {
    return null;
  }

  const ids = new Set<string>();
  const tabs: ContextTab[] = [];

  for (const valueTab of candidate.tabs) {
    if (!valueTab || typeof valueTab !== 'object') return null;
    const tab = valueTab as Partial<ContextTab>;

    if (
      typeof tab.id !== 'string' ||
      tab.id.length === 0 ||
      ids.has(tab.id) ||
      !Array.isArray(tab.history) ||
      tab.history.length === 0 ||
      !tab.history.every(isLocation) ||
      !Number.isInteger(tab.historyIndex) ||
      tab.historyIndex! < 0 ||
      tab.historyIndex! >= tab.history.length ||
      typeof tab.lastActivatedAt !== 'number'
    ) {
      return null;
    }

    const activeLocation = tab.history[tab.historyIndex!];
    ids.add(tab.id);
    tabs.push({
      ...createTab(
        tab.id,
        activeLocation,
        tab.lastActivatedAt,
        tab.history,
        tab.historyIndex,
      ),
      isDirty: false,
    });
  }

  if (!ids.has(candidate.activeTabId)) return null;

  return {
    version: CONTEXT_TABS_SNAPSHOT_VERSION,
    activeTabId: candidate.activeTabId,
    tabs,
  };
}

function mostRecentlyActivated(
  tabs: ContextTab[],
  predicate: (tab: ContextTab) => boolean,
) {
  return tabs
    .filter(predicate)
    .sort((a, b) => b.lastActivatedAt - a.lastActivatedAt)[0];
}

/**
 * Framework-neutral state engine for browser-like application contexts.
 *
 * The hook owns serializable tab identity, per-tab history, exact-page/area
 * focusing, dirty state, and close/limit rules. It intentionally does not read
 * browser storage, call a router, prompt the user, or resolve application
 * routes. Those policies belong to the consuming application's adapter.
 */
export function useContextTabs({
  initialLocation,
  initialSnapshot,
  maxTabs = DEFAULT_CONTEXT_TABS_LIMIT,
  createId = defaultCreateId,
  now = Date.now,
}: UseContextTabsOptions) {
  const [createIdValue] = useState<() => string>(() => createId);
  const [nowValue] = useState<() => number>(() => now);
  const [maxTabsValue] = useState(() => Math.max(1, maxTabs));

  const [snapshot, setSnapshot] = useState<ContextTabsSnapshot>(() => {
    return (
      parseSnapshot(initialSnapshot, maxTabsValue) ??
      createInitialSnapshot(initialLocation, createIdValue, nowValue)
    );
  });
  const snapshotRef = useRef(snapshot);

  const commit = useCallback((next: ContextTabsSnapshot) => {
    snapshotRef.current = next;
    setSnapshot(next);
  }, []);

  const selectTab = useCallback(
    (tabId: string) => {
      const current = snapshotRef.current;
      if (!current.tabs.some((tab) => tab.id === tabId)) return false;

      commit({
        ...current,
        activeTabId: tabId,
        tabs: current.tabs.map((tab) =>
          tab.id === tabId ? { ...tab, lastActivatedAt: nowValue() } : tab,
        ),
      });
      return true;
    },
    [commit, nowValue],
  );

  const addAfterActive = useCallback(
    (
      location: ContextTabLocation,
      history?: ContextTabLocation[],
      historyIndex?: number,
      placement: 'after-active' | 'end' = 'after-active',
    ): ContextTabsCreateResult => {
      const current = snapshotRef.current;
      if (current.tabs.length >= maxTabsValue) {
        return { status: 'max-tabs' };
      }

      const tab = createTab(
        createIdValue(),
        location,
        nowValue(),
        history,
        historyIndex,
      );
      const activeIndex = current.tabs.findIndex(
        (item) => item.id === current.activeTabId,
      );
      const tabs = [...current.tabs];
      if (placement === 'end') tabs.push(tab);
      else tabs.splice(activeIndex + 1, 0, tab);
      commit({ ...current, activeTabId: tab.id, tabs });
      return { status: 'created', tabId: tab.id };
    },
    [commit, createIdValue, maxTabsValue, nowValue],
  );

  const duplicateActiveTab = useCallback((): ContextTabsCreateResult => {
    const current = snapshotRef.current;
    const active = current.tabs.find((tab) => tab.id === current.activeTabId)!;
    return addAfterActive(
      currentLocation(active),
      active.history,
      active.historyIndex,
    );
  }, [addAfterActive]);

  const createFreshTab = useCallback(
    (location: ContextTabLocation): ContextTabsCreateResult =>
      addAfterActive(location),
    [addAfterActive],
  );

  const navigate = useCallback(
    (location: ContextTabLocation, options?: NavigateOptions) => {
      const current = snapshotRef.current;
      const tabs = current.tabs.map((tab) => {
        if (tab.id !== current.activeTabId) return tab;
        const resolvedLocation =
          tab.pageKey === location.pageKey &&
          options?.preserveIdentity !== false
            ? {
                ...location,
                name: tab.name,
                contextLabel: tab.contextLabel ?? location.contextLabel,
                iconKey: tab.iconKey ?? location.iconKey,
              }
            : location;

        if (options?.mode === 'replace') {
          const history = [...tab.history];
          history[tab.historyIndex] = copyLocation(resolvedLocation);
          return withCurrentLocation({ ...tab, history }, resolvedLocation);
        }

        const history = [
          ...tab.history.slice(0, tab.historyIndex + 1),
          copyLocation(resolvedLocation),
        ];
        return withCurrentLocation(
          { ...tab, history, historyIndex: history.length - 1 },
          resolvedLocation,
        );
      });
      commit({ ...current, tabs });
    },
    [commit],
  );

  const moveHistory = useCallback(
    (offset: -1 | 1): ContextTabsHistoryResult => {
      const current = snapshotRef.current;
      const active = current.tabs.find(
        (tab) => tab.id === current.activeTabId,
      )!;
      const historyIndex = active.historyIndex + offset;
      if (historyIndex < 0 || historyIndex >= active.history.length) {
        return { status: 'boundary' };
      }

      const location = active.history[historyIndex];
      commit({
        ...current,
        tabs: current.tabs.map((tab) =>
          tab.id === active.id
            ? withCurrentLocation({ ...tab, historyIndex }, location)
            : tab,
        ),
      });
      return { status: 'navigated', location };
    },
    [commit],
  );

  const focusMatchOrCreate = useCallback(
    (
      predicate: (tab: ContextTab) => boolean,
      location: ContextTabLocation,
    ): ContextTabsCreateResult => {
      const match = mostRecentlyActivated(snapshotRef.current.tabs, predicate);
      if (match) {
        selectTab(match.id);
        return { status: 'focused', tabId: match.id };
      }
      return addAfterActive(location, undefined, undefined, 'end');
    },
    [addAfterActive, selectTab],
  );

  const focusAreaOrCreate = useCallback(
    (area: string, fallbackLocation: ContextTabLocation) =>
      focusMatchOrCreate((tab) => tab.area === area, fallbackLocation),
    [focusMatchOrCreate],
  );

  const focusPageOrCreate = useCallback(
    (location: ContextTabLocation) =>
      focusMatchOrCreate((tab) => tab.pageKey === location.pageKey, location),
    [focusMatchOrCreate],
  );

  const closeTab = useCallback(
    (tabId: string, options?: CloseOptions): ContextTabsCloseResult => {
      const current = snapshotRef.current;
      const index = current.tabs.findIndex((tab) => tab.id === tabId);
      if (index < 0) return { status: 'not-found' };
      if (current.tabs.length === 1) return { status: 'last-tab' };
      if (current.tabs[index].isDirty && !options?.discardDirty) {
        return { status: 'dirty' };
      }

      const tabs = current.tabs.filter((tab) => tab.id !== tabId);
      const activeTabId =
        current.activeTabId === tabId
          ? (tabs[index] ?? tabs[index - 1]).id
          : current.activeTabId;
      commit({ ...current, activeTabId, tabs });
      return { status: 'closed', activeTabId };
    },
    [commit],
  );

  const updateTabMetadata = useCallback(
    (tabId: string, update: MetadataUpdate) => {
      const current = snapshotRef.current;
      if (!current.tabs.some((tab) => tab.id === tabId)) return false;
      const definedUpdate: MetadataUpdate = {};
      if (update.name !== undefined) definedUpdate.name = update.name;
      if (update.section !== undefined) definedUpdate.section = update.section;
      if (update.contextLabel !== undefined) {
        definedUpdate.contextLabel = update.contextLabel;
      }
      if (update.iconKey !== undefined) definedUpdate.iconKey = update.iconKey;

      commit({
        ...current,
        tabs: current.tabs.map((tab) => {
          if (tab.id !== tabId) return tab;
          const next = { ...tab, ...definedUpdate };
          const history = [...next.history];
          history[next.historyIndex] = {
            ...history[next.historyIndex],
            ...definedUpdate,
          };
          return { ...next, history };
        }),
      });
      return true;
    },
    [commit],
  );

  const setTabDirty = useCallback(
    (tabId: string, isDirty: boolean) => {
      const current = snapshotRef.current;
      if (!current.tabs.some((tab) => tab.id === tabId)) return false;
      commit({
        ...current,
        tabs: current.tabs.map((tab) =>
          tab.id === tabId ? { ...tab, isDirty } : tab,
        ),
      });
      return true;
    },
    [commit],
  );

  const activeTab = snapshot.tabs.find(
    (tab) => tab.id === snapshot.activeTabId,
  )!;

  return {
    snapshot,
    activeTab,
    canAddTab: snapshot.tabs.length < maxTabsValue,
    canCloseTab: snapshot.tabs.length > 1,
    canGoBack: activeTab.historyIndex > 0,
    canGoForward: activeTab.historyIndex < activeTab.history.length - 1,
    selectTab,
    createTab: createFreshTab,
    duplicateActiveTab,
    navigate,
    goBack: () => moveHistory(-1),
    goForward: () => moveHistory(1),
    focusAreaOrCreate,
    focusPageOrCreate,
    closeTab,
    updateTabMetadata,
    setTabDirty,
  };
}
